import React, { useState, useRef, useEffect, useSyncExternalStore } from "react";

interface TooltipProps {
  content: string;
  children: React.ReactElement;
  disabled?: boolean;
}

const mobileQuery = "(max-width: 639px)";
function subscribeMobile(cb: () => void) {
  const mql = window.matchMedia(mobileQuery);
  mql.addEventListener("change", cb);
  return () => mql.removeEventListener("change", cb);
}
function getSnapshotMobile() { return window.matchMedia(mobileQuery).matches; }
function getServerSnapshotMobile() { return false; }
function useIsMobile() {
  return useSyncExternalStore(subscribeMobile, getSnapshotMobile, getServerSnapshotMobile);
}

/**
 * Custom tooltip component with immediate hover response
 * Displays a styled overlay with tooltip text on hover
 */
export default function Tooltip({
  content,
  children,
  disabled = false,
}: TooltipProps): JSX.Element {
  const isMobile = useIsMobile();
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const targetRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Calculate tooltip position on mouse enter
  const handleMouseEnter = () => {
    if (disabled || isMobile || !content) return;

    setIsVisible(true);

    if (targetRef.current) {
      const rect = targetRef.current.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

      // Position tooltip to the top-left of the element
      setPosition({
        top: rect.top + scrollTop - 10, // 10px above the element
        left: rect.left + scrollLeft,
      });
    }
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  // No need to adjust position - tooltip stays at top-left

  return (
    <>
      <div
        ref={targetRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ display: "inline-block", width: "100%" }}
      >
        {children}
      </div>

      {isVisible && content && (
        <div
          ref={tooltipRef}
          className="fixed z-50 px-3 py-2 text-xs text-gray-900 bg-white rounded-lg border border-gray-300 max-w-xs pointer-events-none"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            transform: "translateY(-100%) translateY(-8px)",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
          }}
        >
          {content}
          {/* Wikipedia-style speech bubble tail */}
          <div
            className="absolute"
            style={{
              left: "24px",
              bottom: "-6px",
              width: "12px",
              height: "6px",
            }}
          >
            <svg
              width="12"
              height="6"
              viewBox="0 0 12 6"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* Outer border path */}
              <path
                d="M 0 0 L 6 6 L 12 0 Z"
                fill="white"
                stroke="#d1d5db"
                strokeWidth="1"
              />
              {/* Inner white fill to cover border on top edge */}
              <path
                d="M 1 0 L 6 5 L 11 0 Z"
                fill="white"
              />
            </svg>
          </div>
        </div>
      )}
    </>
  );
}
